/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { announcementsAPI, workingGroupsAPI } from '../lib/api';
import { formatDateTime, formatRelativeTime, TARGET_AUDIENCES, KENT_KONSEYI_ANA_KADEME, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import EmailNotifyToggle from '../components/EmailNotifyToggle';
import {
  Megaphone,
  Plus, FileDown,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Bell,
  Loader2,
  Building,
  Send,
  Shield,
  Lock,
  Search,
  X,
  Filter,
  CalendarDays,
  Users,
  Paperclip,
  UploadCloud,
  FileText,
  Image as ImageIcon,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  Pencil,
  MapPin,
} from 'lucide-react';

// Dosya eki konfigurasyonu (backend ile ayni)
const ALLOWED_FILE_EXTS = ['pdf','doc','docx','xls','xlsx','ppt','pptx','jpg','jpeg','png'];
const MAX_FILE_SIZE_MB = 50;
const MAX_FILES = 10;
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
};

const AnnouncementsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Duyurular');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const limit = 10;

  const isAdmin = ['admin', 'genel_sekreter', 'meclis_baskani', 'muhtar'].includes(user?.role);
  const canApprove = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'].includes(user?.role);

  const hasActiveFilters = searchQuery || audienceFilter || dateFrom || dateTo;

  useEffect(() => {
    loadAnnouncements();
  }, [page, priorityFilter, audienceFilter, dateFrom, dateTo]);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit,
        limit,
        priority: priorityFilter || undefined,
        search: searchQuery || undefined,
        target_audience: audienceFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await announcementsAPI.getAll(params);
      setAnnouncements(response.data.announcements);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Duyurular yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadAnnouncements();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPriorityFilter('');
    setAudienceFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const getPriorityBadge = (priority) => {
    const priorities = {
      dusuk: { label: 'Düşük', class: 'bg-slate-50 text-slate-700 border-slate-200' },
      normal: { label: 'Normal', class: 'bg-blue-50 text-blue-700 border-blue-200' },
      yuksek: { label: 'Yüksek', class: 'bg-amber-50 text-amber-700 border-amber-200' },
      acil: { label: 'Acil', class: 'bg-red-50 text-red-700 border-red-200' },
    };
    const info = priorities[priority] || priorities.normal;
    return <Badge className={`${info.class} border`}>{info.label}</Badge>;
  };

  const totalPages = Math.ceil(total / limit);

  const handleApprove = async (id, e) => {
    e.stopPropagation();
    try {
      await announcementsAPI.approve(id);
      toast.success('Duyuru yayinlandi');
      loadAnnouncements();
    } catch { toast.error('Onaylama basarisiz'); }
  };
  const handleReject = async (id, e) => {
    e.stopPropagation();
    const reason = window.prompt('Reddetme sebebi (opsiyonel):') || '';
    try {
      await announcementsAPI.reject(id, reason);
      toast.success('Duyuru reddedildi');
      loadAnnouncements();
    } catch { toast.error('Reddetme basarisiz'); }
  };
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu duyuruyu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await announcementsAPI.delete(id);
      toast.success('Duyuru silindi');
      loadAnnouncements();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Silme başarısız');
    }
  };

  return (
    <div className="space-y-6" data-testid="announcements-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Duyurular</h1>
          <p className="text-slate-500 text-sm">Kent Konseyi duyurularini goruntuleyebilirsiniz</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { const a=document.createElement('a'); a.href=`${process.env.REACT_APP_BACKEND_URL}/api/announcements/export/excel`; a.click(); }} data-testid="export-announcements-btn">
              <FileDown className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button onClick={() => navigate('/panel/duyurular/yeni')} data-testid="new-announcement-btn" className="shadow-sm">
              <Plus className="w-4 h-4 mr-2" /> Yeni Duyuru
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {/* Arama + Filtre Toggle */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative" data-testid="ann-search-form">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Baslik, icerik veya yazar ara..."
              className="pl-9 pr-9 h-9"
              data-testid="ann-search-input"
            />
            {searchQuery && (
              <button type="button" onClick={() => { setSearchQuery(''); setPage(1); setTimeout(loadAnnouncements, 0); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          <Button variant={showFilters ? "default" : "outline"} size="sm" onClick={() => setShowFilters(!showFilters)}
            className={`h-9 gap-1.5 ${showFilters ? '' : ''}`} data-testid="ann-toggle-filters-btn">
            <Filter className="w-3.5 h-3.5" />
            Filtrele
            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-blue-400 ml-1" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-slate-500 hover:text-red-600" data-testid="ann-clear-filters-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Temizle
            </Button>
          )}
        </div>

        {/* Oncelik Pill'leri */}
        <div className="flex items-center gap-2 flex-wrap">
          {['', 'acil', 'yuksek', 'normal', 'dusuk'].map(val => {
            const labels = { '': 'Tumu', acil: 'Acil', yuksek: 'Yuksek', normal: 'Normal', dusuk: 'Dusuk' };
            const active = priorityFilter === val;
            const colors = {
              '': active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200',
              acil: active ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-600 border-red-200',
              yuksek: active ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-600 border-amber-200',
              normal: active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-600 border-blue-200',
              dusuk: active ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-slate-500 border-slate-200',
            };
            return (
              <button key={val} onClick={() => { setPriorityFilter(val); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${colors[val]}`}
                data-testid={`filter-${val || 'all'}`}>{labels[val]}</button>
            );
          })}
          <span className="ml-auto text-xs text-slate-400">{total} duyuru</span>
        </div>

        {/* Genisletilmis Filtreler */}
        {showFilters && (
          <Card className="shadow-sm border-slate-200" data-testid="ann-advanced-filters">
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Hedef Kitle
                  </Label>
                  <Select value={audienceFilter} onValueChange={(v) => { setAudienceFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-9" data-testid="ann-audience-filter">
                      <SelectValue placeholder="Tum hedef kitleler" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tum Hedef Kitleler</SelectItem>
                      {TARGET_AUDIENCES.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> Baslangic Tarihi
                  </Label>
                  <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                    className="h-9" data-testid="ann-date-from" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> Bitis Tarihi
                  </Label>
                  <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                    className="h-9" data-testid="ann-date-to" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : announcements.length > 0 ? (
        <>
          <div className="space-y-3">
            {announcements.map((a) => {
              const borderC = { acil: 'border-l-red-500', yuksek: 'border-l-amber-500', normal: 'border-l-blue-500', dusuk: 'border-l-slate-300' };
              const iconBg = { acil: 'bg-red-50 text-red-600', yuksek: 'bg-amber-50 text-amber-600', normal: 'bg-blue-50 text-blue-600', dusuk: 'bg-slate-50 text-slate-500' };
              return (
                <div key={a.id} className={`group bg-white rounded-lg border border-slate-200 border-l-4 ${borderC[a.priority] || 'border-l-blue-500'} shadow-sm hover:shadow-md transition-all cursor-pointer`}
                  onClick={() => navigate(`/panel/duyurular/${a.id}`)} data-testid={`announcement-card-${a.id}`}>
                  <div className="p-5 flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg[a.priority] || 'bg-blue-50 text-blue-600'}`}>
                      {a.priority === 'acil' ? <AlertTriangle className="w-5 h-5" /> : <Megaphone className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{a.title}</h3>
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                            <span>{a.created_by_name}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span>{formatRelativeTime(a.created_at)}</span>
                          </p>
                        </div>
                        {getPriorityBadge(a.priority)}
                      </div>
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{a.content}</p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {a.approval_status === 'onay_bekliyor' && (
                          <Badge className="text-xs h-5 px-2 bg-amber-100 text-amber-800 border border-amber-300" data-testid={`approval-pending-${a.id}`}>
                            <Clock className="w-3 h-3 mr-1" /> Onay Bekliyor
                          </Badge>
                        )}
                        {a.approval_status === 'reddedildi' && (
                          <Badge className="text-xs h-5 px-2 bg-red-50 text-red-700 border border-red-200">
                            Reddedildi
                          </Badge>
                        )}
                        {a.mahalle_name && (
                          <Badge className="text-xs h-5 px-2 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`mahalle-badge-${a.id}`}>
                            <MapPin className="w-3 h-3 mr-1" /> {a.mahalle_name} Mahallesi
                          </Badge>
                        )}
                        {a.target_audience?.slice(0, 3).map(t => (
                          <Badge key={t} variant="outline" className="text-xs font-normal h-5 px-2 border-slate-200 text-slate-500">
                            {TARGET_AUDIENCES.find(ta => ta.value === t)?.label || t}
                          </Badge>
                        ))}
                        {a.target_audience?.length > 3 && <Badge variant="outline" className="text-xs h-5 px-2 border-slate-200 text-slate-400">+{a.target_audience.length - 3}</Badge>}
                        {a.target_group_ids?.length > 0 && (
                          <Badge className="text-xs h-5 px-2 bg-amber-50 text-amber-700 border border-amber-200"><Lock className="w-3 h-3 mr-1" /> Grup Kisitli</Badge>
                        )}
                        {canApprove && a.approval_status === 'onay_bekliyor' && (
                          <div className="ml-auto flex gap-1.5 flex-wrap">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/panel/duyurular/${a.id}/duzenle`); }} data-testid={`edit-announcement-${a.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                            </Button>
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={(e) => handleApprove(a.id, e)} data-testid={`approve-announcement-${a.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Onayla
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" onClick={(e) => handleReject(a.id, e)} data-testid={`reject-announcement-${a.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reddet
                            </Button>
                          </div>
                        )}
                        {!canApprove && a.approval_status === 'onay_bekliyor' && a.created_by === user?.id && (
                          <div className="ml-auto flex gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/panel/duyurular/${a.id}/duzenle`); }} data-testid={`edit-announcement-${a.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={(e) => handleDelete(a.id, e)} data-testid={`delete-announcement-${a.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                            </Button>
                          </div>
                        )}
                        {canApprove && a.approval_status !== 'onay_bekliyor' && (
                          <div className="ml-auto flex gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/panel/duyurular/${a.id}/duzenle`); }} data-testid={`edit-announcement-${a.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={(e) => handleDelete(a.id, e)} data-testid={`delete-announcement-${a.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                            </Button>
                          </div>
                        )}
                        {!canApprove && (
                          <Eye className="w-4 h-4 text-slate-300 ml-auto group-hover:text-blue-500 transition-colors" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-slate-400">Sayfa {page}/{totalPages}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (pNum > totalPages) return null;
                  return <Button key={pNum} variant={pNum === page ? "default" : "outline"} size="sm" onClick={() => setPage(pNum)} className="h-8 w-8 p-0 text-xs">{pNum}</Button>;
                })}
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4"><Megaphone className="w-7 h-7 text-slate-300" /></div>
            <h3 className="text-base font-semibold text-slate-900">Henuz duyuru yok</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">{isAdmin ? 'Ilk duyuruyu olusturmak icin butona tiklayin' : 'Henuz duyuru yayinlanmadi'}</p>
            {isAdmin && <Button onClick={() => navigate('/panel/duyurular/yeni')} className="mt-4" size="sm"><Plus className="w-4 h-4 mr-2" /> Yeni Duyuru</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const NewAnnouncementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { announcementId } = useParams();
  const isEditMode = Boolean(announcementId);
  usePageTitle(isEditMode ? 'Duyuru Düzenle' : 'Yeni Duyuru');
  const [loading, setLoading] = useState(false);
  const [loadingDuyuru, setLoadingDuyuru] = useState(isEditMode);
  const [meclisler, setMeclisler] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [mahalleAdi, setMahalleAdi] = useState('');
  const isMuhtar = user?.role === 'muhtar';
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    target_audience: isMuhtar ? ['muhtarlar'] : [],
    target_group_ids: [],
    priority: 'normal',
    expires_at: '',
  });
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAudiences, setEmailAudiences] = useState([]);

  // Dosya ekleri - once local queue, duyuru olusturulunca backende upload
  const [pendingFiles, setPendingFiles] = useState([]); // [{file, error}]
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState({});

  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `${formatFileSize(file.size)} - maks ${MAX_FILE_SIZE_MB} MB`;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_FILE_EXTS.includes(ext)) {
      return `'.${ext}' desteklenmiyor`;
    }
    // Cift uzanti on kontrol
    const parts = file.name.toLowerCase().split('.');
    if (parts.length >= 3) {
      const dangerous = ['exe','bat','cmd','sh','js','vbs','jar','py'];
      for (let i = 1; i < parts.length; i++) {
        if (dangerous.includes(parts[i])) {
          return `Cift uzanti tespit edildi (.${parts[i]})`;
        }
      }
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (pendingFiles.length + newFiles.length > MAX_FILES) {
      toast.error(`En fazla ${MAX_FILES} dosya ekleyebilirsiniz`);
      return;
    }
    const validated = newFiles.map(f => ({ file: f, error: validateFile(f) }));
    const withErrors = validated.filter(v => v.error);
    if (withErrors.length > 0) {
      withErrors.forEach(w => toast.error(`${w.file.name}: ${w.error}`));
    }
    setPendingFiles(prev => [...prev, ...validated.filter(v => !v.error)]);
    e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const showGroupSelector = !isMuhtar && (formData.target_audience.includes('calisma_gruplari') || formData.target_audience.includes('meclisler'));

  const isAnaKademeSelected = KENT_KONSEYI_ANA_KADEME.every(v => formData.target_audience.includes(v));

  const toggleAnaKademe = () => {
    if (isAnaKademeSelected) {
      setFormData({
        ...formData,
        target_audience: formData.target_audience.filter(a => !KENT_KONSEYI_ANA_KADEME.includes(a)),
      });
    } else {
      setFormData({
        ...formData,
        target_audience: [...new Set([...formData.target_audience, ...KENT_KONSEYI_ANA_KADEME])],
      });
    }
  };

  useEffect(() => {
    if (isMuhtar && user?.mahalle_id && user?.ilce_id) {
      import('../lib/api').then(({ locationsAPI }) => {
        locationsAPI.getMahalleler(user.ilce_id).then(res => {
          const m = res.data.find(x => x.id === user.mahalle_id);
          if (m) setMahalleAdi(m.name);
        }).catch(() => {});
      });
    }
  }, [isMuhtar, user]);

  useEffect(() => {
    if (showGroupSelector && meclisler.length === 0) {
      loadMeclislerAndGroups();
    }
  }, [showGroupSelector]);

  const loadMeclislerAndGroups = async () => {
    setLoadingGroups(true);
    try {
      const resp = await workingGroupsAPI.getMeclisler();
      setMeclisler(resp.data);
      const groups = [];
      for (const m of resp.data) {
        const detail = await workingGroupsAPI.getMeclisDetail(m.id);
        for (const wg of (detail.data.working_groups || [])) {
          groups.push({ ...wg, kind: 'calisma_grubu', meclis_name: m.name, meclis_id: m.id });
        }
        for (const yk of (detail.data.yurutme_kurulu_list || [])) {
          groups.push({ ...yk, kind: 'yurutme_kurulu', meclis_name: m.name, meclis_id: m.id });
        }
      }
      setAllGroups(groups);
    } catch (e) {
      console.error('Failed to load groups:', e);
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroupId = (id) => {
    const current = formData.target_group_ids;
    if (current.includes(id)) {
      setFormData({ ...formData, target_group_ids: current.filter(g => g !== id) });
    } else {
      setFormData({ ...formData, target_group_ids: [...current, id] });
    }
  };

  // Sadece meclisi toggle et
  const toggleMeclisOnly = (meclisId) => {
    const current = formData.target_group_ids;
    if (current.includes(meclisId)) {
      setFormData({ ...formData, target_group_ids: current.filter(id => id !== meclisId) });
    } else {
      setFormData({ ...formData, target_group_ids: [...current, meclisId] });
    }
  };

  // Meclis + alt tum YK ve CG'ler
  const toggleAllInMeclis = (meclisId) => {
    const subGroups = allGroups.filter(g => g.meclis_id === meclisId).map(g => g.id);
    const allIds = [meclisId, ...subGroups];
    const current = formData.target_group_ids;
    const allSelected = allIds.every(id => current.includes(id));
    if (allSelected) {
      setFormData({ ...formData, target_group_ids: current.filter(id => !allIds.includes(id)) });
    } else {
      setFormData({ ...formData, target_group_ids: [...new Set([...current, ...allIds])] });
    }
  };

  // Edit mode: mevcut duyuruyu yukle
  useEffect(() => {
    if (!isEditMode) {
      setLoadingDuyuru(false);
      return;
    }
    setLoadingDuyuru(true);
    (async () => {
      try {
        const r = await announcementsAPI.getById(announcementId);
        const a = r.data;
        setFormData({
          title: a.title || '',
          content: a.content || '',
          target_audience: a.target_audience || [],
          target_group_ids: a.target_group_ids || [],
          priority: a.priority || 'normal',
          expires_at: a.expires_at ? String(a.expires_at).slice(0, 10) : '',
        });
      } catch {
        toast.error('Duyuru yüklenemedi');
        navigate('/panel/duyurular');
      } finally {
        setLoadingDuyuru(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementId, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Lütfen başlık ve içerik girin');
      return;
    }
    
    if (!isMuhtar && formData.target_audience.length === 0) {
      toast.error('En az bir hedef kitle seçin');
      return;
    }

    // Edit mode: PUT ile guncelle
    if (isEditMode) {
      setLoading(true);
      try {
        await announcementsAPI.update(announcementId, {
          ...formData,
          target_group_ids: formData.target_group_ids.length > 0 ? formData.target_group_ids : null,
          send_notification_email: false,
          email_target_audiences: null,
        });
        toast.success('Duyuru güncellendi');
        navigate('/panel/duyurular');
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Duyuru güncellenemedi');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      const isMuhtar = user?.role === 'muhtar';
      // 1) Duyuruyu olustur (dosyalar sonra upload edilecek, sonra eger email isteniyorsa send notification flag'iyle update yerine
      //    send_notification_email'i false birakip, dosyalar eklendikten sonra email'i ayri tetikleyecegiz)
      const willSendEmail = !isMuhtar && sendEmail;
      const data = {
        ...formData,
        target_group_ids: formData.target_group_ids.length > 0 ? formData.target_group_ids : null,
        // Dosya ekleri varsa, once dosyalar yuklenecek sonra email gonderilecek
        // Bu yuzden create API'sinde send_notification_email=false gonderiyoruz, sonra tekrar duyuru update ile true yapacagiz
        send_notification_email: willSendEmail && pendingFiles.length === 0,
        email_target_audiences: (willSendEmail) ? (emailAudiences.length > 0 ? emailAudiences : formData.target_audience) : null,
      };
      const response = await announcementsAPI.create(data);
      const annId = response.data.id;

      // 2) Dosyalari yukle
      if (pendingFiles.length > 0) {
        toast.info(`${pendingFiles.length} dosya yükleniyor...`);
        let successCount = 0;
        for (let i = 0; i < pendingFiles.length; i++) {
          setUploadingIdx(i);
          try {
            await announcementsAPI.uploadAttachment(annId, pendingFiles[i].file, (pct) => {
              setUploadProgress(p => ({ ...p, [i]: pct }));
            });
            successCount++;
          } catch (uploadErr) {
            toast.error(`${pendingFiles[i].file.name}: ${formatApiErrorDetail(uploadErr.response?.data?.detail) || 'yüklenemedi'}`);
          }
        }
        setUploadingIdx(-1);
        toast.success(`${successCount}/${pendingFiles.length} dosya yüklendi`);

        // 3) Dosyalar yuklendikten sonra email gonderilecekse, simdi tetikle
        if (willSendEmail) {
          try {
            await announcementsAPI.sendNotificationEmail(annId, {
              email_target_audiences: emailAudiences.length > 0 ? emailAudiences : formData.target_audience,
            });
            toast.info('E-posta bildirimi eklerinizle birlikte arka planda gönderiliyor');
          } catch (emailErr) {
            toast.error('E-posta bildirimi tetiklenirken hata: ' + (formatApiErrorDetail(emailErr.response?.data?.detail) || ''));
          }
        }
      }

      toast.success('Duyuru yayınlandı');
      navigate('/panel/duyurular');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Duyuru yayınlanamadı');
    } finally {
      setLoading(false);
    }
  };

  const toggleAudience = (value) => {
    if (formData.target_audience.includes(value)) {
      setFormData({
        ...formData,
        target_audience: formData.target_audience.filter(a => a !== value),
      });
    } else {
      setFormData({
        ...formData,
        target_audience: [...formData.target_audience, value],
      });
    }
  };

  if (isEditMode && loadingDuyuru) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-announcement-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/panel/duyurular')} className="text-slate-600 hover:text-slate-900">
            <ChevronLeft className="w-4 h-4 mr-1" /> Duyurulara Don
          </Button>
        </div>
      </div>

      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{isEditMode ? 'Duyuru Düzenle' : 'Yeni Duyuru Olustur'}</h1>
              <p className="text-sm text-white/50">Duyuru detaylarini doldurun ve yayinlayin</p>
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol - Ana Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Baslik & Icerik */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                  Duyuru Icerigi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-sm font-medium">Baslik *</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Duyuru basligi yazin" required data-testid="announcement-title-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="content" className="text-sm font-medium">Icerik *</Label>
                  <Textarea id="content" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Duyuru icerigini detayli olarak yazin..." rows={8} required data-testid="announcement-content-input" className="mt-1.5" />
                </div>
              </CardContent>
            </Card>

            {/* Hedef Kitle */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                  Hedef Kitle
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Duyuruyu gorecek gruplari secin</p>
              </CardHeader>
              <CardContent>
                {isMuhtar ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="muhtar-mahalle-info">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <Lock className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">Mahalle Duyurusu</p>
                        <p className="text-xs text-emerald-700 mt-0.5">
                          Bu duyuru sadece <span className="font-bold">{mahalleAdi || 'mahallenize'}</span> ait kullanicilara gosterilecektir.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {TARGET_AUDIENCES.map((audience) => {
                      const checked = formData.target_audience.includes(audience.value);
                      return (
                        <button key={audience.value} type="button" onClick={() => toggleAudience(audience.value)}
                          data-testid={`audience-${audience.value}`}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${checked ? 'border-blue-500 bg-blue-50 text-blue-800 font-medium' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                            {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          {audience.label}
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" onClick={toggleAnaKademe}
                    data-testid="audience-ana-kademe-btn"
                    className={`w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-semibold transition-all ${isAnaKademeSelected ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-300 bg-slate-50 text-slate-600 hover:border-indigo-400 hover:bg-indigo-50/50'}`}>
                    <Building className={`w-4 h-4 ${isAnaKademeSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                    Kent Konseyi Ana Kademe
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isAnaKademeSelected ? 'bg-indigo-200 text-indigo-800' : 'bg-slate-200 text-slate-500'}`}>
                      {isAnaKademeSelected ? 'Secildi' : 'Toplu Sec'}
                    </span>
                  </button>
                  {isAnaKademeSelected && (
                    <p className="text-xs text-indigo-600 text-center">Yonetim Kurulu + Genel Sekreter + Kent Konseyi Baskani secildi</p>
                  )}
                </div>
                )}
              </CardContent>
            </Card>

            {/* Grup Hedefleme */}
            {showGroupSelector && (
              <Card className="shadow-sm border-amber-200">
                <CardHeader className="pb-3 bg-amber-50/50">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
                    <Shield className="w-5 h-5 text-amber-600" /> Meclis / Calisma Grubu Hedefleme
                  </CardTitle>
                  <p className="text-xs text-amber-700 mt-1">Sectiginiz gruplara yalnizca onayli uyeler erisebilir. Bos birakirsaniz tum rol grubuna acik olur.</p>
                </CardHeader>
                <CardContent className="pt-4" data-testid="ann-group-selector-section">
                  {loadingGroups ? (
                    <div className="flex items-center gap-2 py-6 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                      <span className="text-sm text-amber-700">Gruplar yukleniyor...</span>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {meclisler.map((meclis) => {
                        const subGroups = allGroups.filter(g => g.meclis_id === meclis.id);
                        const mGroups = subGroups.filter(g => g.kind === 'calisma_grubu');
                        const mYKs = subGroups.filter(g => g.kind === 'yurutme_kurulu');
                        const subIds = subGroups.map(g => g.id);
                        const meclisChecked = formData.target_group_ids.includes(meclis.id);
                        const allChecked = [meclis.id, ...subIds].every(id => formData.target_group_ids.includes(id));
                        return (
                          <div key={meclis.id} className="border border-amber-100 rounded-lg bg-white p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox id={`ann-meclis-${meclis.id}`} checked={meclisChecked} onCheckedChange={() => toggleMeclisOnly(meclis.id)} data-testid={`ann-meclis-checkbox-${meclis.id}`} />
                                <label htmlFor={`ann-meclis-${meclis.id}`} className="text-sm font-bold text-amber-900 cursor-pointer">{meclis.name}</label>
                              </div>
                              {subGroups.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleAllInMeclis(meclis.id)}
                                  className="text-[10px] text-amber-700 hover:text-amber-900 hover:underline px-2 py-0.5"
                                  data-testid={`ann-meclis-toggle-all-${meclis.id}`}
                                >
                                  {allChecked ? 'Tumunu temizle' : 'Tumunu sec (Meclis + YK + CG)'}
                                </button>
                              )}
                            </div>
                            {mYKs.length > 0 && (
                              <div className="ml-6 mb-2 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Yurutme Kurulu</p>
                                {mYKs.map(yk => (
                                  <div key={yk.id} className="flex items-center space-x-2">
                                    <Checkbox id={`ann-group-${yk.id}`} checked={formData.target_group_ids.includes(yk.id)} onCheckedChange={() => toggleGroupId(yk.id)} data-testid={`ann-group-checkbox-${yk.id}`} />
                                    <label htmlFor={`ann-group-${yk.id}`} className="text-xs text-emerald-800 cursor-pointer">{yk.name}</label>
                                  </div>
                                ))}
                              </div>
                            )}
                            {mGroups.length > 0 && (
                              <div className="ml-6 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Calisma Gruplari</p>
                                {mGroups.map(g => (
                                  <div key={g.id} className="flex items-center space-x-2">
                                    <Checkbox id={`ann-group-${g.id}`} checked={formData.target_group_ids.includes(g.id)} onCheckedChange={() => toggleGroupId(g.id)} data-testid={`ann-group-checkbox-${g.id}`} />
                                    <label htmlFor={`ann-group-${g.id}`} className="text-xs text-slate-700 cursor-pointer">{g.name}</label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {formData.target_group_ids.length > 0 && (
                    <p className="text-xs text-amber-700 mt-2 font-medium">{formData.target_group_ids.length} grup/meclis secildi</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sag - Ayarlar Sidebar */}
          <div className="space-y-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                  Ayarlar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="priority" className="text-sm font-medium">Oncelik</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger data-testid="announcement-priority-select" className="mt-1.5">
                      <SelectValue placeholder="Oncelik secin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dusuk">Dusuk</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="yuksek">Yuksek</SelectItem>
                      <SelectItem value="acil">Acil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="expires_at" className="text-sm font-medium">Bitis Tarihi</Label>
                  <Input id="expires_at" type="date" value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })} className="mt-1.5" />
                  <p className="text-xs text-slate-400 mt-1.5">Bos birakilirsa suresiz yayinda kalir</p>
                </div>
              </CardContent>
            </Card>

            {/* Onizleme */}
            <Card className="shadow-sm border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-500">Onizleme</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-8 rounded-full ${formData.priority === 'acil' ? 'bg-red-500' : formData.priority === 'yuksek' ? 'bg-amber-500' : formData.priority === 'normal' ? 'bg-blue-500' : 'bg-slate-300'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{formData.title || 'Duyuru basligi...'}</p>
                      <p className="text-xs text-slate-400 truncate">{formData.content ? formData.content.substring(0, 60) + '...' : 'Icerik...'}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.target_audience.map(a => (
                      <Badge key={a} variant="outline" className="text-xs h-5 px-1.5">{TARGET_AUDIENCES.find(t => t.value === a)?.label || a}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* E-posta Bildirim - Muhtarlara kesinlikle gosterme */}
            {user?.role !== 'muhtar' && (
              <EmailNotifyToggle
                sendEmail={sendEmail} setSendEmail={setSendEmail}
                emailAudiences={emailAudiences} setEmailAudiences={setEmailAudiences}
                defaultAudiences={formData.target_audience}
              />
            )}

            {/* Dosya Ekleri */}
            <Card className="border-slate-200">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                      <Paperclip className="w-4 h-4" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold block">Dosya Ekleri</Label>
                      <p className="text-xs text-slate-500">PDF · Word · Excel · PowerPoint · JPG · PNG</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">{pendingFiles.length}/{MAX_FILES}</Badge>
                </div>

                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-amber-400 transition-colors bg-slate-50/50">
                  <label className="flex flex-col items-center gap-2 cursor-pointer" data-testid="file-dropzone">
                    <UploadCloud className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-600 font-medium">Dosya seç</span>
                    <span className="text-xs text-slate-400">Maks. {MAX_FILE_SIZE_MB} MB / dosya · {MAX_FILES} dosya limiti</span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={loading || pendingFiles.length >= MAX_FILES}
                      data-testid="file-input"
                    />
                  </label>
                </div>

                {pendingFiles.length > 0 && (
                  <div className="space-y-1.5" data-testid="pending-files-list">
                    {pendingFiles.map((item, idx) => {
                      const isImg = ['jpg','jpeg','png'].includes((item.file.name.split('.').pop() || '').toLowerCase());
                      const isUploading = uploadingIdx === idx;
                      const progress = uploadProgress[idx] || 0;
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-md">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${isImg ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                            {isImg ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-700 truncate" data-testid={`file-name-${idx}`}>{item.file.name}</p>
                            <p className="text-[10px] text-slate-500">{formatFileSize(item.file.size)}</p>
                            {isUploading && (
                              <div className="mt-1 h-1 bg-slate-200 rounded overflow-hidden">
                                <div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
                              </div>
                            )}
                          </div>
                          {!loading && (
                            <button
                              type="button"
                              onClick={() => removePendingFile(idx)}
                              className="text-slate-400 hover:text-red-500 p-1"
                              data-testid={`remove-file-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {sendEmail && pendingFiles.length > 0 && (
                  <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded p-2 flex items-start gap-1.5">
                    <Paperclip className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>E-posta alıcılarına dosyalar <strong>link olarak</strong> gönderilir (ek değil). Alıcılar indirmek için giriş yapmalıdır.</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-2">
              <Button type="submit" disabled={loading} className="w-full shadow-sm" data-testid="submit-announcement-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {isEditMode ? 'Değişiklikleri Kaydet' : 'Duyuru Yayinla'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/panel/duyurular')} className="w-full">
                Iptal
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export { AnnouncementsListPage, NewAnnouncementPage };
